import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { raceEventService } from '../services/raceService';
import { cloudAnalysisService } from '../services/cloudAnalysisService';
import { calculateLapsForAthlete } from '../services/raceAnalysis';
import { calculateMatchesBurned } from '../services/matchesAnalysis';
import { ArrowLeftIcon } from '../components/icons/PlaybackIcons';
import type { RaceEvent, RaceCategory, Athlete } from '../types';
import { 
    computeSectorLeaderboard, 
    computeSectorStats, 
    formatSectorTime, 
    getSectorAthleteTelemetry 
} from '../utils/raceSector';
import { 
    AthleteRaceContext, 
    generateAthletePrompt, 
    generateGroupRidePrompt 
} from '../utils/insightPrompts';
import { renderMarkdown } from '../utils/markdownRenderer';
import { enrichAthletesWithUserProfile } from '../services/draftingAnalysis';

const formatTime = (seconds: number) => {
    if (isNaN(seconds) || seconds < 0) return '0m 0s';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
}

const MobileRaceVisualizer: React.FC = () => {
    const { raceId, categoryId } = useParams<{ raceId: string; categoryId: string }>();
    const navigate = useNavigate();
    const { currentUser, userProfile } = useAuth();

    const [raceEvent, setRaceEvent] = useState<RaceEvent | null>(null);
    const [activeCategory, setActiveCategory] = useState<RaceCategory | null>(null);
    const [userAthlete, setUserAthlete] = useState<Athlete | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Drafting & AI State
    const [draftingStats, setDraftingStats] = useState<any | null>(null);
    const [aiDebrief, setAiDebrief] = useState<string | null>(null);
    const [aiError, setAiError] = useState<string | null>(null);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);

    // Always ensure page opens at the top
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [raceId, categoryId]);

    // 1. Fetch Race Data
    useEffect(() => {
        if (!raceId) return;
        const fetchRace = async () => {
            try {
                let event = await raceEventService.getRaceEventBySlug(raceId);
                
                // Fallback for legacy URLs
                if (!event) {
                    const legacy = await raceEventService.findParentByLegacyCategoryId(raceId);
                    if (legacy) {
                        navigate(`/race/${legacy.parentId}/category/${legacy.categoryId}`, { replace: true });
                        return;
                    }
                }

                if (event) {
                    setRaceEvent(event);

                    const currentRacePath = event.slug || event.id;
                    const category = categoryId 
                        ? (event.categories.find(c => c.id === categoryId || c.slug === categoryId) || event.categories[0])
                        : event.categories[0];
                    const currentCategoryPath = category ? (category.slug || category.id) : '';
                    
                    setActiveCategory(category);

                    // Find the logged-in user's athlete data
                    if (currentUser && category) {
                        const athlete = category.athletes.find(a => a.userId === currentUser.uid || a.id === currentUser.uid);
                        setUserAthlete(athlete || null);
                    }

                    // Canonical redirect: if they visited via ID but slugs exist, or if they didn't specify category
                    if (category && (raceId !== currentRacePath || categoryId !== currentCategoryPath)) {
                        navigate(`/race/${currentRacePath}/category/${currentCategoryPath}`, { replace: true });
                        return;
                    }
                }
            } catch (e) {
                console.error("Failed to load race", e);
            } finally {
                setIsLoading(false);
            }
        };
        fetchRace();
    }, [raceId, categoryId, currentUser, navigate]);

    // 2. Fetch Drafting Stats (Only once data is loaded)
    useEffect(() => {
        if (!userAthlete || !activeCategory || !raceId) return;
        
        const fetchDrafting = async () => {
            try {
                const enrichedAthletes = enrichAthletesWithUserProfile(activeCategory.athletes, currentUser?.uid, userProfile);
                const results = await cloudAnalysisService.getDraftingStats(enrichedAthletes, raceId);
                const userDrafting = results.find((r: any) => r.athleteId === userAthlete.id);
                if (userDrafting) setDraftingStats(userDrafting);
            } catch (e) {
                console.error("Failed to fetch drafting stats", e);
            }
        };
        fetchDrafting();
    }, [userAthlete, activeCategory, raceId, currentUser?.uid, userProfile]);

    // 3. Calculate Lap & Course Data
    const { laps, avgSpeedMph, totalTimeFormatted } = useMemo(() => {
        if (!userAthlete || !userAthlete.track || userAthlete.track.length < 2 || !raceEvent?.course) {
            return { laps: [], totalDistanceMiles: 0, avgSpeedMph: 0, totalTimeFormatted: '00:00' };
        }

        const calculatedLaps = calculateLapsForAthlete(userAthlete.track, raceEvent.course.start);

        // Calculate overall averages
        const totalTimeMs = userAthlete.track[userAthlete.track.length - 1].timestamp - userAthlete.track[0].timestamp;
        const totalDistanceMeters = userAthlete.track[userAthlete.track.length - 1].distance;
        const totalDistanceMiles = totalDistanceMeters * 0.000621371;
        
        const avgSpeedMph = (totalDistanceMeters / (totalTimeMs / 1000)) * 2.23694; // m/s to mph
        
        const mins = Math.floor(totalTimeMs / 60000);
        const secs = Math.floor((totalTimeMs % 60000) / 1000);
        const totalTimeFormatted = `${mins}:${secs.toString().padStart(2, '0')}`;

        return { laps: calculatedLaps, totalDistanceMiles, avgSpeedMph, totalTimeFormatted };
    }, [userAthlete, raceEvent]);

    // Matchbook Values
    const matchEvents = useMemo(() => {
        const dbEvents = userAthlete?.matchEvents || [];
        // If the database has stale match data (missing the new 'activeBurnDuration' field),
        // recalculate it on the fly so the user instantly sees the new detailed metrics!
        if (dbEvents.length > 0 && dbEvents[0].activeBurnDuration === undefined && userAthlete?.track && userProfile?.ftp) {
            return calculateMatchesBurned(userAthlete.track, userProfile.ftp);
        }
        return dbEvents;
    }, [userAthlete, userProfile]);

    // Group ride: the rider's Race Sector effort and rank among that day's riders
    const isGroupRide = raceEvent?.eventType === 'group_ride';
    const raceSector = raceEvent?.groupRideDetails?.spiritedSegment;
    const sectorResult = useMemo(() => {
        if (!isGroupRide || !raceSector || !activeCategory || !userAthlete) return null;
        const { rows } = computeSectorLeaderboard(activeCategory.athletes, raceSector, raceEvent?.course?.path);
        const mine = rows.find(r => r.athleteId === userAthlete.id) || null;
        return { mine, total: rows.length };
    }, [isGroupRide, raceSector, activeCategory, userAthlete, raceEvent?.course?.path]);
    const fullSectorLeaderboard = useMemo(() => {
        if (!isGroupRide || !raceSector || !activeCategory) return null;
        return computeSectorLeaderboard(activeCategory.athletes, raceSector, raceEvent?.course?.path);
    }, [isGroupRide, raceSector, activeCategory, raceEvent?.course?.path]);

    const showLapSplits = !isGroupRide || !!raceEvent?.isCircuit;
    const rideDateLabel = (() => {
        const dateStr = raceEvent?.occurrenceDate || raceEvent?.date;
        if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr || '';
        const [y, m, d] = dateStr.split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    })();

    const matchesBurned = matchEvents.length;
    const estimatedMatches = userProfile?.estimatedMatches || ((userProfile?.racesAnalyzedForMatches || 0) < 5 ? 4 : 'Unknown');

    const sectorStats = useMemo(() => {
        if (!isGroupRide || !raceSector) return null;
        return computeSectorStats(
            raceEvent?.course?.path,
            raceEvent?.groupRideDetails?.elevationProfile,
            raceSector
        );
    }, [isGroupRide, raceSector, raceEvent?.course?.path, raceEvent?.groupRideDetails?.elevationProfile]);

    const effectiveSectorTelemetry = useMemo(() => {
        if (!userAthlete || !isGroupRide || !raceSector) return null;
        return getSectorAthleteTelemetry(
            userAthlete,
            raceSector,
            raceEvent?.course?.path,
            userProfile?.weightLbs
        );
    }, [userAthlete, isGroupRide, raceSector, raceEvent?.course?.path, userProfile?.weightLbs]);

    // 4. Generate AI Debrief
    const handleGenerateDebrief = async () => {
        if (!userAthlete || !raceEvent) return;
        setIsGeneratingAI(true);
        setAiError(null);
        try {
            const raceData: AthleteRaceContext = {
                name: raceEvent.name,
                category: activeCategory?.name || (isGroupRide ? 'Group Ride' : 'Event'),
                date: raceEvent.occurrenceDate || raceEvent.date || '',
                course: raceEvent.course,
                athletes: activeCategory?.athletes || []
            };

            const prompt = isGroupRide
                ? generateGroupRidePrompt(
                    userAthlete,
                    raceData,
                    raceEvent.groupRideDetails,
                    effectiveSectorTelemetry,
                    sectorResult?.mine || undefined,
                    sectorResult?.total || fullSectorLeaderboard?.rows.length || 0,
                    sectorStats?.distanceMiles ?? null,
                    sectorStats?.elevationGainFt ?? null,
                    userProfile
                )
                : generateAthletePrompt(
                    userAthlete,
                    raceData,
                    userProfile,
                    undefined
                );

            const result = await cloudAnalysisService.generateAIInsights(prompt);
            setAiDebrief(result);
            setAiError(null);
        } catch (error: any) {
            console.error("AI Generation failed", error);
            const msg = error?.message || '';
            const isRateLimit = msg.includes('60 seconds') || msg.includes('wait') || error?.code === 'resource-exhausted' || error?.status === 429;
            setAiError(isRateLimit 
                ? 'Rate limit reached. Please wait a moment before trying again.' 
                : 'Failed to generate insights. Please try again later.'
            );
        } finally {
            setIsGeneratingAI(false);
        }
    };

    const handleBack = () => {
        if (window.history.state && typeof window.history.state.idx === 'number' && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            navigate('/races');
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-900 text-cyan-500">
                <svg className="animate-spin h-10 w-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
        );
    }

    if (!userAthlete) {
        return (
            <div className="min-h-screen bg-gray-900 text-gray-100 pb-12">
                {/* Header */}
                <div className="bg-gray-800 border-b border-gray-700 p-4 sticky top-0 z-10 flex items-center gap-3">
                    <button onClick={handleBack} className="p-2 bg-gray-700 rounded-full text-white hover:bg-gray-600 transition-colors">
                        <ArrowLeftIcon className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-lg font-bold leading-tight">{raceEvent?.name}</h1>
                        <p className="text-xs text-cyan-400 font-medium">
                            {isGroupRide ? `Group Ride Replay • ${rideDateLabel}` : `Race Replay • ${activeCategory?.name}`}
                        </p>
                    </div>
                </div>

                <div className="p-4 space-y-4">
                    {/* Guest / Non-participant CTA */}
                    {!currentUser ? (
                        <div className="p-5 bg-gradient-to-br from-cyan-950/60 via-gray-800 to-gray-800 border border-cyan-500/40 rounded-2xl shadow-lg space-y-3">
                            <div className="flex items-center gap-2 text-cyan-400">
                                <span className="text-xl">🚴</span>
                                <h2 className="text-sm font-bold uppercase tracking-wider text-cyan-300">Were you on this ride?</h2>
                            </div>
                            <p className="text-xs text-gray-300 leading-relaxed">
                                Create a free account to upload your GPS file and unlock your personal performance debrief, drafting savings, and power insights.
                            </p>
                            <div className="space-y-2 pt-1">
                                <Link
                                    to={`/auth?mode=signup&returnTo=${encodeURIComponent(window.location.pathname)}`}
                                    className="w-full inline-flex items-center justify-center px-4 py-3 bg-cyan-500 hover:bg-cyan-400 text-gray-950 font-bold rounded-xl text-sm transition-all shadow-md shadow-cyan-500/20 active:scale-95"
                                >
                                    Create Account to Upload
                                </Link>
                                <div className="text-center">
                                    <Link
                                        to={`/auth?returnTo=${encodeURIComponent(window.location.pathname)}`}
                                        className="text-xs text-gray-400 hover:text-cyan-400 transition-colors"
                                    >
                                        Already have an account? <span className="text-cyan-400 font-semibold underline">Sign In</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-5 bg-gray-800/80 border border-gray-700 rounded-2xl shadow-lg text-center space-y-2">
                            <h2 className="text-sm font-bold text-white uppercase tracking-wider">No Personal Telemetry</h2>
                            <p className="text-xs text-gray-400 leading-relaxed">
                                You don't have a ride file matched to this week. Upload your file from a desktop browser to get your debrief!
                            </p>
                        </div>
                    )}

                    {/* Sector Leaderboard if present */}
                    {isGroupRide && fullSectorLeaderboard && fullSectorLeaderboard.rows.length > 0 && (
                        <div className="bg-gray-800/90 rounded-2xl border border-gray-700 p-5 shadow-lg space-y-3">
                            <div className="flex items-center justify-between border-b border-gray-700 pb-2">
                                <h3 className="text-xs font-bold uppercase tracking-wider text-cyan-400 flex items-center gap-1.5">
                                    <span>⚡</span> Race Sector Leaderboard
                                </h3>
                                <span className="text-xs text-gray-400">{fullSectorLeaderboard.rows.length} {fullSectorLeaderboard.rows.length === 1 ? 'rider' : 'riders'}</span>
                            </div>
                            <div className="space-y-2">
                                {fullSectorLeaderboard.rows.map(row => (
                                    <div 
                                        key={row.athleteId} 
                                        className="w-full flex items-center justify-between p-2.5 bg-gray-900/60 rounded-xl border border-gray-700/60 text-xs"
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <span className="w-5 text-center font-bold text-cyan-400">{row.rank}</span>
                                            <div>
                                                <div className="font-semibold text-white">{row.name}</div>
                                                <div className="text-[11px] text-gray-400">
                                                    {(row.effort.avgSpeedMps * 2.23694).toFixed(1)} mph
                                                    {row.effort.avgPower !== null ? ` • ${Math.round(row.effort.avgPower)}W` : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right font-mono font-bold text-cyan-300">
                                            {formatSectorTime(row.effort.elapsedMs)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Athletes list if no sector leaderboard */}
                    {(!fullSectorLeaderboard || fullSectorLeaderboard.rows.length === 0) && activeCategory?.athletes && activeCategory.athletes.length > 0 && (
                        <div className="bg-gray-800/90 rounded-2xl border border-gray-700 p-5 shadow-lg space-y-3">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-cyan-400 border-b border-gray-700 pb-2">
                                Riders ({activeCategory.athletes.length})
                            </h3>
                            <div className="space-y-2">
                                {activeCategory.athletes.map(ath => (
                                    <div key={ath.id} className="p-2.5 bg-gray-900/60 rounded-xl border border-gray-700/60 flex items-center justify-between text-xs">
                                        <span className="font-semibold text-white">{ath.name}</span>
                                        <span className="text-[11px] text-gray-400">Rider</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 pb-12">
            {/* Header */}
            <div className="bg-gray-800 border-b border-gray-700 p-4 sticky top-0 z-10 flex items-center gap-3">
                <button onClick={handleBack} className="p-2 bg-gray-700 rounded-full text-white">
                    <ArrowLeftIcon className="w-5 h-5" />
                </button>
                <div>
                    <h1 className="text-lg font-bold leading-tight">{raceEvent?.name}</h1>
                    <p className="text-xs text-cyan-400 font-medium">
                        {isGroupRide ? `Group Ride Debrief • ${rideDateLabel}` : `Post-Race Debrief • ${activeCategory?.name}`}
                    </p>
                </div>
            </div>

            <div className="p-4 space-y-4">
                {/* CARD 1: The Headline */}
                <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 p-5 rounded-2xl border border-gray-700 shadow-lg">
                    <h2 className="text-sm text-gray-400 font-bold uppercase tracking-wider mb-4">Performance Summary</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-3xl font-extrabold text-white">{avgSpeedMph.toFixed(1)} <span className="text-sm text-gray-400 font-normal">mph</span></p>
                            <p className="text-xs text-gray-500 mt-1">Average Speed</p>
                        </div>
                        <div>
                            <p className="text-3xl font-extrabold text-white">{totalTimeFormatted}</p>
                            <p className="text-xs text-gray-500 mt-1">Total Moving Time</p>
                        </div>
                    </div>
                </div>

                {/* Group ride: Race Sector effort */}
                {isGroupRide && raceSector && (
                    <div className="bg-cyan-950/40 p-5 rounded-2xl border border-cyan-500/40 shadow-lg">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-lg">⚡</span>
                            <h2 className="text-sm text-cyan-300 font-bold uppercase tracking-wider">Race Sector</h2>
                        </div>
                        {sectorResult?.mine ? (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-3xl font-extrabold text-white">{formatSectorTime(sectorResult.mine.effort.elapsedMs)}</p>
                                    <p className="text-xs text-cyan-200/70 mt-1">Sector Time</p>
                                </div>
                                <div>
                                    <p className="text-3xl font-extrabold text-white">{sectorResult.mine.rank} <span className="text-sm text-gray-400 font-normal">/ {sectorResult.total}</span></p>
                                    <p className="text-xs text-cyan-200/70 mt-1">Rank Today</p>
                                </div>
                                <div>
                                    <p className="text-xl font-bold text-white">{(sectorResult.mine.effort.avgSpeedMps * 2.23694).toFixed(1)} <span className="text-sm text-gray-400 font-normal">mph</span></p>
                                    <p className="text-xs text-cyan-200/70 mt-1">Avg Speed</p>
                                </div>
                                <div>
                                    <p className="text-xl font-bold text-white">{sectorResult.mine.effort.avgPower !== null ? `${Math.round(sectorResult.mine.effort.avgPower)}` : '—'} <span className="text-sm text-gray-400 font-normal">W</span></p>
                                    <p className="text-xs text-cyan-200/70 mt-1">Avg Power</p>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-cyan-200/70 italic">Your file doesn't include a complete pass through the Race Sector.</p>
                        )}
                    </div>
                )}

                {/* CARD 2: Drafting Analytics */}
                <div className="bg-cyan-900/20 p-5 rounded-2xl border border-cyan-500/30 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                            <h2 className="text-sm text-cyan-400 font-bold uppercase tracking-wider">Drafting Analysis</h2>
                        </div>
                        {draftingStats?.isEstimated && (
                            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-cyan-900/60 text-cyan-300 border border-cyan-500/30">
                                Estimated
                            </span>
                        )}
                    </div>
                    
                    {draftingStats ? (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-2xl font-extrabold text-white">{formatTime(draftingStats.totalTimeDrafting || 0)}</p>
                                <p className="text-xs text-cyan-200/70 mt-1">Time Drafting</p>
                            </div>
                            <div>
                                <p className="text-2xl font-extrabold text-white">{(draftingStats.totalEnergySavedKj || 0).toFixed(0)}<span className="text-sm text-cyan-200/70 ml-1 font-normal">kJ</span></p>
                                <p className="text-xs text-cyan-200/70 mt-1">Energy Saved</p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-cyan-200/70 italic">Processing drafting stats...</p>
                    )}
                </div>

                {/* CARD 3: Matchbook Summary */}
                <div className="bg-orange-900/20 p-5 rounded-2xl border border-orange-500/30 shadow-lg">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="text-xl">🔥</span>
                        <h2 className="text-sm text-orange-400 font-bold uppercase tracking-wider">Matchbook Summary</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-3xl font-extrabold text-white">{matchesBurned} <span className="text-sm text-gray-400 font-normal">/ {estimatedMatches}</span></p>
                            <p className="text-xs text-orange-200/70 mt-1">Matches Burned</p>
                        </div>
                    </div>
                    {matchEvents.length > 0 && (
                        <div className="mt-4 space-y-2">
                            {matchEvents.map((m, idx) => {
                                const startTimeMs = userAthlete.track[0]?.timestamp || 0;
                                const relativeTimeSecs = Math.max(0, (m.time - startTimeMs) / 1000);
                                const mins = Math.floor(relativeTimeSecs / 60);
                                const secs = Math.floor(relativeTimeSecs % 60).toString().padStart(2, '0');
                                const timeString = `${mins}:${secs}`;
                                
                                const totalDurSecs = Math.round(m.durationSeconds);
                                const durMins = Math.floor(totalDurSecs / 60);
                                const durSecs = totalDurSecs % 60;
                                const minLabel = durMins === 1 ? 'min' : 'mins';
                                const formattedDuration = durMins > 0 ? `${durMins} ${minLabel} ${durSecs} seconds` : `${durSecs} seconds`;

                                return (
                                    <div key={idx} className="flex flex-col p-3 bg-gray-900/50 rounded-lg border border-orange-900/30 gap-1.5">
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs text-gray-300 font-bold">Match {idx + 1} <span className="text-gray-500 font-normal ml-1">@ {timeString}</span></span>
                                            <span className="text-xs text-orange-400 font-bold">Peak: {m.maxPower ? Math.round(m.maxPower) : Math.round(m.avgPower)}W</span>
                                        </div>
                                        {m.activeBurnDuration ? (
                                            <div className="flex flex-col gap-0.5">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[10px] text-gray-300 font-medium">
                                                        Active Effort: {Math.round(m.activeBurnDuration) >= 60 ? `${Math.floor(Math.round(m.activeBurnDuration) / 60)}:${(Math.round(m.activeBurnDuration) % 60).toString().padStart(2, '0')}` : `${Math.round(m.activeBurnDuration)}s`}
                                                    </span>
                                                    <span className="text-[10px] text-orange-300 font-mono font-bold">Avg: {Math.round(m.activeBurnAvgPower!)}W</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[9px] text-gray-500">Burn Window: {formattedDuration}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] text-gray-500">Burn Window: {formattedDuration}</span>
                                                <span className="text-[10px] text-orange-300/70 font-mono">Avg: {Math.round(m.avgPower)}W</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Group ride: Full Race Sector Leaderboard */}
                {isGroupRide && fullSectorLeaderboard && fullSectorLeaderboard.rows.length > 0 && (
                    <div className="bg-gray-800/90 rounded-2xl border border-gray-700 p-5 shadow-lg space-y-3">
                        <div className="flex items-center justify-between border-b border-gray-700 pb-2">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-cyan-400 flex items-center gap-1.5">
                                <span>⚡</span> Race Sector Leaderboard
                            </h3>
                            <span className="text-xs text-gray-400">{fullSectorLeaderboard.rows.length} {fullSectorLeaderboard.rows.length === 1 ? 'rider' : 'riders'}</span>
                        </div>
                        <div className="space-y-2">
                            {fullSectorLeaderboard.rows.map(row => {
                                const isMe = userAthlete && row.athleteId === userAthlete.id;
                                return (
                                    <div 
                                        key={row.athleteId} 
                                        className={`w-full flex items-center justify-between p-2.5 rounded-xl border text-xs ${
                                            isMe 
                                                ? 'bg-cyan-950/40 border-cyan-500/40' 
                                                : 'bg-gray-900/60 border-gray-700/60'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <span className={`w-5 text-center font-bold ${isMe ? 'text-cyan-300' : 'text-cyan-400'}`}>{row.rank}</span>
                                            <div>
                                                <div className="font-semibold text-white flex items-center gap-1.5">
                                                    <span>{row.name}</span>
                                                    {isMe && <span className="text-[10px] text-cyan-300 font-bold px-1.5 py-0.5 rounded bg-cyan-500/20 border border-cyan-500/40">You</span>}
                                                </div>
                                                <div className="text-[11px] text-gray-400">
                                                    {(row.effort.avgSpeedMps * 2.23694).toFixed(1)} mph
                                                    {row.effort.avgPower !== null ? ` • ${Math.round(row.effort.avgPower)}W` : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right font-mono font-bold text-cyan-300">
                                            {formatSectorTime(row.effort.elapsedMs)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* CARD 4: AI Coach / Group Ride & Sector Debrief */}
                <div className="bg-gray-800 p-5 rounded-2xl border border-gray-700 shadow-lg">
                    <h2 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
                        {isGroupRide ? (
                            <>
                                <span className="text-cyan-400 text-base">⚡</span>
                                <span className="text-white">Group Ride & Sector Debrief</span>
                            </>
                        ) : (
                            <>
                                <span className="text-gray-300">AI Coach Insights</span>
                                <span className="bg-purple-500/20 text-purple-400 text-[10px] px-2 py-0.5 rounded-full border border-purple-500/30">Beta</span>
                            </>
                        )}
                    </h2>
                    
                    {aiError && (
                        <div className="text-red-400 bg-red-900/40 border border-red-800/60 p-3 rounded-xl mb-4 flex items-center justify-between gap-3 text-xs">
                            <span className="leading-relaxed">{aiError}</span>
                            <button
                                onClick={handleGenerateDebrief}
                                disabled={isGeneratingAI}
                                className="px-2.5 py-1 bg-red-800/80 hover:bg-red-700 text-white rounded text-xs font-semibold whitespace-nowrap transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {!aiDebrief ? (
                        <div className="text-center py-2">
                            <p className="text-sm text-gray-400 mb-4">
                                {isGroupRide
                                    ? 'Get an authoritative tactical breakdown of your rollout conservation, pack dynamics, and race sector performance.'
                                    : 'Get a personalized tactical breakdown of your race metrics.'}
                            </p>
                            <button 
                                onClick={handleGenerateDebrief}
                                disabled={isGeneratingAI}
                                className={`w-full font-bold py-3 rounded-xl active:scale-95 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${
                                    isGroupRide
                                        ? 'bg-cyan-500 hover:bg-cyan-400 text-gray-950 shadow-cyan-500/20'
                                        : 'bg-white hover:bg-gray-100 text-gray-900'
                                }`}
                            >
                                {isGeneratingAI 
                                    ? (isGroupRide ? 'Analyzing Telemetry & Sector...' : 'Analyzing Telemetry...') 
                                    : (isGroupRide ? 'Analyze Group Ride & Sector' : 'Get Short Debrief')}
                            </button>
                        </div>
                    ) : (
                        <div className="animate-fade-in space-y-4">
                            <div 
                                className="prose prose-sm prose-invert max-w-none text-gray-200 text-sm leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: renderMarkdown(aiDebrief) }}
                            />
                        </div>
                    )}
                </div>

                {/* CARD 5: Lap Breakdown */}
                {showLapSplits && laps.length > 0 && (
                    <div className="bg-gray-800 p-5 rounded-2xl border border-gray-700 shadow-lg">
                        <h2 className="text-sm text-gray-400 font-bold uppercase tracking-wider mb-4">Lap Splits</h2>
                        <div className="space-y-2">
                            {laps.map((lap, idx) => {
                                const mins = Math.floor(lap.lapTime / 60000);
                                const secs = Math.floor((lap.lapTime % 60000) / 1000);
                                const speed = ((lap.avgSpeed || 0) * 0.621371).toFixed(1);
                                
                                return (
                                    <div key={idx} className="flex justify-between items-center p-3 bg-gray-900/50 rounded-lg border border-gray-700/50">
                                        <div className="flex items-center gap-3">
                                            <span className="text-gray-500 font-mono text-sm">{idx + 1}</span>
                                            <span className="text-white font-medium">{mins}:{secs.toString().padStart(2, '0')}</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-sm text-cyan-400 font-medium">{speed} mph</span>
                                            {lap.avgPower && <span className="text-xs text-gray-500 ml-2">{Math.round(lap.avgPower)}W</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileRaceVisualizer;